import type { TFunction } from "i18next";
import type { ValueKind } from "../../../api/content/authoring/selectorDecoders";
import { classText, itemKindText, sentenceCase, spaced, stepKindClass, termDefinition } from "../../../api/content/processTermWords";
import type { DataTypeRef } from "../../../api/content/workflowsApi";
import type { SourceItemKind } from "../model/graphSources";

/**
 * Types in words, the one wording for every card, port, plan and editor (F40, F61; ADR-0032
 * D1; V23): a data type, a kind of item and a kind of step by its class's label in the DMW
 * process ontology, as the server serves it (`processTermWords.ts`, which words a kind and a
 * class for every layer); a project's class by its local name; and the value kinds older
 * definitions name.
 */
export { classText, itemKindText, spaced } from "../../../api/content/processTermWords";

/** Value kinds in words, as `portType` says them: as a Thread names them. */
export function kindsText(kinds: readonly ValueKind[], t: TFunction): string {
  return unionText(
    kinds.map((kind) => t(`threads.toolTurn.valueKinds.${kind}`)),
    t,
  );
}

/** Data types in words (ADR-0032 D1): each by its class's label; "several …" where it takes or gives many. */
export function typesText(types: readonly DataTypeRef[], t: TFunction): string {
  return unionText(
    types.map((reference) => {
      const word = classText(reference.type, t);
      return reference.many ? t("workflows.node.port.many", { type: word }) : word;
    }),
    t,
  );
}

/**
 * Data types' hint (V23): each type's definition in the process ontology, such as "A whole
 * number." for `xsd:integer`, shown on hover and focus beside the standard's name; `null`
 * where the ontology defines none of them.
 */
export function typesHint(types: readonly DataTypeRef[], t: TFunction): string | null {
  const definitions = [...new Set(types.flatMap((reference) => termDefinition(reference.type, t) ?? []))];
  return definitions.length === 0 ? null : definitions.join(" ");
}

function unionText(words: readonly string[], t: TFunction): string {
  const last = words.at(-1);
  if (last === undefined) {
    return t("workflows.node.port.typeNotDeclared");
  }
  return words.length === 1 ? last : t("workflows.node.port.typeUnion", { kinds: words.slice(0, -1).join(", "), last });
}

/** A kind of item in words, as the review and the library name it. */
export function itemTypeText({ kind, many }: SourceItemKind, t: TFunction): string {
  const word = itemKindText(kind, t);
  return many ? t("workflows.node.port.many", { type: word }) : word;
}

/** A step definition kind (`agent`, `human`, `tool`) by its class's label. */
export function stepKindText(kind: string, t: TFunction): string {
  const iri = stepKindClass(kind);
  return iri === null ? sentenceCase(spaced(kind)) : classText(iri, t);
}
